import React, { FC, useRef, useEffect } from 'react';
import clsx from 'clsx';
import {
  Button,
  Eyebrow,
  Heading,
  Image,
  headingType,
  size,
  Badge,
  EyebrowEnum,
  useWindowSize,
} from '@marriott/mi-ui-library';
import { useSession } from '@marriott/mi-headless-utils';
import { AuthoringUtils } from '@adobe/aem-spa-page-model-manager';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import { CardVariation, MerchandisingCardTileProps } from './MerchandisingCardTile.types';
import { StyledMerchandisingCardTile } from './MerchandisingCardTile.styles';
import { getTrackingPropertiesDynamic, isExternalLink, BadgeVariation, updateCarousel } from '../../utils/helpers';

export const MerchandisingCardTileComponent: FC<MerchandisingCardTileProps> = props => {
  const { datalayer } = useSession();
  const {
    mboxParameter,
    metaData,
    logo,
    eyebrowText,
    promotionalTag,
    destinationUrl1,
    headerMedium,
    tileSquareVariation,
    trackingProperties = {},
    image,
    openInNewTab,
    isTargetContentLoaded = mboxParameter && !AuthoringUtils.isInEditor() ? false : true,
    isTargetContentError = false,
    displayEvergreenContent = true,
    styleclass,
    reRenderContainer = () => {},
    ariaLabel = headerMedium,
  } = props;
  const { width = 0 } = useWindowSize();
  const ref = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    if (props.mboxParameter && !isTargetContentLoaded && String(displayEvergreenContent) === 'false') {
      reRenderContainer();
    }
    if (isTargetContentLoaded) {
      if (isTargetContentError && String(displayEvergreenContent) !== 'true') {
        updateCarousel(ref, false); // means will be removeed from carousel;
      } else updateCarousel(ref);
    }
  }, [isTargetContentLoaded]);

  const trackingPropertiesDynamic = getTrackingPropertiesDynamic(
    trackingProperties,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    metaData as unknown as any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    props as unknown as any,
    datalayer?.['mr_prof_authentication_state'] === 'authenticated',
    isTargetContentError,
    mboxParameter
  );

  const isWideVariation = tileSquareVariation === CardVariation.Wide;

  return (
    <StyledMerchandisingCardTile
      data-testid="merch-MerchandisingCardTile"
      data-component-name="o-merch-MerchandisingCardTile"
      className={clsx(
        `justify-content-between ${
          isTargetContentError && String(displayEvergreenContent) === 'false' ? 'd-none' : 'd-flex'
        }`
      )}
    >
      {isTargetContentLoaded ? (
        <Button
          isLink={true}
          href={destinationUrl1}
          target={isExternalLink(destinationUrl1, openInNewTab) ? '_blank' : '_self'}
          ref={ref}
          ariaLabel={ariaLabel}
          className={`merchandising-card-tile arrow-icon ${
            metaData?.theme ? (metaData?.theme !== 'Dark' ? 'standard' : 'inverse') : styleclass
          } ${
            isExternalLink(destinationUrl1, openInNewTab)
              ? 'icon-arrow-right-external icon-l'
              : 'icon-arrow-right icon-l'
          }
         `}
          trackingProperties={trackingPropertiesDynamic}
        >
          <div
            className={`merchandising-card-tile__container ${
              styleclass?.includes('inverse') ? 'inverse' : 'standard'
            } `}
            data-variation={tileSquareVariation}
            data-testid="card-tile"
          >
            <Image
              customClass={clsx(
                'merchandising-card-tile__container__image',
                tileSquareVariation === CardVariation.SquareV1 ||
                  tileSquareVariation === CardVariation.SquareV2 ||
                  tileSquareVariation === CardVariation.SquareV3
                  ? 'merchandising-card-tile__container__image-square'
                  : ''
              )}
              altText={image?.altText ?? ''}
              renditions={image?.renditions}
              dynamic={image?.dynamic}
              defaultImageURL={image?.assetPath}
            />
            {tileSquareVariation === CardVariation.SquareV3 && promotionalTag && (
              <div
                data-testid="badge"
                className={clsx(
                  'merchandising-card-tile__container__overlay d-flex justify-content-between align-items-center m-4'
                )}
              >
                <Badge
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  badgeVariation={BadgeVariation.Overlay}
                  badgeText={promotionalTag}
                  customClass="merchandising-card-tile__container__overlay__badge p-2 t-font-xs text-nowrap t-rc-text-transform-uppercase"
                />
              </div>
            )}

            <div className={clsx('merchandising-card-tile__container__content d-flex flex-column justify-content-end')}>
              <div
                className={clsx(
                  'merchandising-card-tile__container__content__card-texts pl-4 py-4 d-flex flex-column justify-content-end',
                  tileSquareVariation !== CardVariation.Wide ? 'mt-2' : ''
                )}
              >
                {tileSquareVariation === CardVariation.SquareV3 && logo && (
                  <Image
                    customClass={clsx('merchandising-card-tile__container__content__card-texts__logo mb-3')}
                    altText={logo?.altText ?? ''}
                    dynamic={false}
                    title={logo?.title}
                    defaultImageURL={logo?.imageSrc}
                    loading={'lazy'}
                  />
                )}

                <div data-testid="eyebrow" className="d-flex">
                  {tileSquareVariation !== CardVariation.SquareV1 && eyebrowText && (
                    <Eyebrow
                      text={eyebrowText}
                      variation={EyebrowEnum.Overlay}
                      customClass="merchandising-card-tile__container__content__card-texts__eyebrow m-ellipsis-1line"
                    />
                  )}
                  <span className={clsx(isWideVariation ? 'col-2' : 'col-3')}></span>
                </div>
                <div data-testid="heading" className="d-flex mt-2">
                  <Heading
                    disableCustomClickTrack
                    variation={headingType.subtitle}
                    titleText={headerMedium || ''}
                    fontSize={isWideVariation ? (width > 992 ? size.extraLarge : size.large) : size.extraLarge}
                    customClass={clsx(
                      `merchandising-card-tile__container__content__card-texts__header t m-0 pr-2 pl-0 m-ellipsis-2lines`,
                      isWideVariation ? 'col-10' : 'col-9 p-0'
                    )}
                  />
                  <span className={clsx(isWideVariation ? 'col-2' : 'col-3')}></span>
                </div>
              </div>
            </div>
          </div>
        </Button>
      ) : (
        <div
          className={clsx(
            isWideVariation ? 'merchandising-card-tile__wide-image' : 'merchandising-card-tile__square-image',
            'skeleton-loader'
          )}
        ></div>
      )}
    </StyledMerchandisingCardTile>
  );
};

const MerchandisingCardTileConfig = {
  emptyLabel: 'MerchandisingCardTile',
  isEmpty: () => true,
  resourceType: `${process.env['NEXT_PUBLIC_AEM_SITE']}/components/content/tilesquare`,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const MerchandisingCardTile = (props: any) => {
  return (
    <EditableComponent
      config={MerchandisingCardTileConfig}
      {...props}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      children={<MerchandisingCardTileComponent {...({} as any)} />}
    />
  );
};
